import { z } from 'zod'
import { DOCUMENT_CATEGORIES } from './documents.types'

export const WIZARD_STEPS = {
  files: 'Add files',
  details: 'Details',
  georeference: 'Georeference',
  permissions: 'Permissions',
  done: 'Done',
} as const

export type WizardStep = keyof typeof WIZARD_STEPS

// Edit skips the files/done steps — no batch to report on, Save just closes the dialog.
export const EDIT_WIZARD_STEPS = Object.fromEntries(
  Object.entries(WIZARD_STEPS).filter(([key]) => key !== 'files' && key !== 'done'),
) as Omit<typeof WIZARD_STEPS, 'files' | 'done'>

export type EditWizardStep = keyof typeof EDIT_WIZARD_STEPS

const GeoreferenceSchema = z.object({
  type: z.enum(['NONE', 'POINT', 'POLYGON']),
  geometry: z
    .union([
      z.object({ type: z.literal('Point'), coordinates: z.tuple([z.number(), z.number()]) }),
      z.object({
        type: z.literal('Polygon'),
        coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))),
      }),
    ])
    .nullable(),
  searchLabel: z.string().nullable(),
})

// Shared metadata + georeference + permissions fields — identical for Upload and Edit.
const DocumentDetailsSchema = z.object({
  category: z
    .union([z.literal(''), z.enum(DOCUMENT_CATEGORIES)])
    .refine((v) => v !== '', { message: 'Category is required' }),
  descriptionMd: z.string().optional(),
  organisation: z.string().optional(),
  source: z.string().optional(),
  publishedAt: z.union([z.literal(''), z.iso.date('Enter a valid date')]).optional(),
  notesMd: z.string().optional(),
  tags: z.array(z.string()),
  // Collection membership is set via separate endpoints, not part of this payload.
  collectionId: z.string().nullable(),
  georeference: GeoreferenceSchema,
  visibility: z.enum(['organisation', 'personal']),
  roleIds: z.array(z.string()),
})

const TitleSchema = z
  .string()
  .min(1, 'Title is required')
  .max(255, 'Title must be 255 characters or fewer')

const DocumentFileEntrySchema = z.object({
  file: z.instanceof(File),
  title: TitleSchema,
  status: z.enum(['idle', 'uploading', 'success', 'error']),
  errorMessage: z.string().nullable(),
})

export const DocumentUploadSchema = DocumentDetailsSchema.extend({
  files: z.array(DocumentFileEntrySchema).min(1, 'Add at least one file'),
}).superRefine((data, ctx) => {
  if (data.visibility === 'organisation' && data.roleIds.length === 0) {
    ctx.addIssue({ code: 'custom', path: ['roleIds'], message: 'Select at least one role' })
  }
})

export const DocumentEditSchema = DocumentDetailsSchema.extend({
  title: TitleSchema,
})

export type DocumentUploadFormData = z.infer<typeof DocumentUploadSchema>
export type DocumentEditFormData = z.infer<typeof DocumentEditSchema>
export type DocumentFileEntry = z.infer<typeof DocumentFileEntrySchema>

export const DEFAULT_UPLOAD_VALUES: DocumentUploadFormData = {
  files: [],
  category: '',
  descriptionMd: '',
  organisation: '',
  source: '',
  publishedAt: '',
  notesMd: '',
  tags: [],
  collectionId: null,
  georeference: { type: 'NONE', geometry: null, searchLabel: null },
  visibility: 'personal',
  roleIds: [],
}
